import * as Popover from "@radix-ui/react-popover";
import { format, type Locale } from "date-fns";
import { enUS } from "date-fns/locale";
import {
    type KeyboardEvent,
    forwardRef,
    useEffect,
    useId,
    useState,
} from "react";
import { DayPicker, type DateRange } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { Button } from "../Button";
import { cx } from "../../utils/cx";
import Icon from "../../../base/components/Icon";
import styles from "./DateRangePicker.module.scss";

const CalendarIcon = () => <Icon icon="calendar" />;

export type DateRangeTrigger = "field" | "button";

export type DateRangeValue = {
    from?: Date;
    to?: Date;
};

export interface DateRangePickerProps {
    /** Floating label / empty-button label (default `Date`). */
    label?: string;
    /**
     * Controlled range. Pass `null` / empty for no selection.
     * Omit with `onChange` omitted for uncontrolled (`defaultValue`).
     */
    value?: DateRangeValue | null;
    defaultValue?: DateRangeValue;
    /** Fires when the range is applied (or immediately if footer is off). */
    onChange?: (range: DateRangeValue | undefined) => void;
    helperText?: string;
    error?: boolean | string;
    disabled?: boolean;
    required?: boolean;
    fullWidth?: boolean;
    className?: string;
    /** `date-fns` pattern for each day (default `MM/dd/yyyy`). */
    dateFormat?: string;
    /** Disable days before this date (e.g. today for Closures). */
    fromDate?: Date;
    toDate?: Date;
    name?: string;
    id?: string;
    locale?: Locale;
    /** Default `2` — matches production dual-month calendar. */
    numberOfMonths?: 1 | 2;
    /**
     * When true (default), selection applies on Apply only — matches
     * production Cancel / Apply footer.
     */
    showFooterActions?: boolean;
    footerLabels?: { apply?: string; cancel?: string };
    /**
     * Trigger chrome:
     * - `field` (default) — floating-label input shell (forms / Closures)
     * - `button` — compact filter chip button (e.g. Order History Range)
     */
    trigger?: DateRangeTrigger;
}

/**
 * Treez DS — DateRangePicker
 *
 * Dual-month range calendar with Cancel / Apply (production parity for
 * Closures). Trigger: floating-label field (`trigger="field"`) or compact
 * filter button (`trigger="button"`).
 */
export const DateRangePicker = forwardRef<
    HTMLButtonElement,
    DateRangePickerProps
>(function DateRangePicker(
    {
        label = "Date",
        value,
        defaultValue,
        onChange,
        helperText,
        error,
        disabled = false,
        required,
        fullWidth = false,
        className,
        dateFormat = "MM/dd/yyyy",
        fromDate,
        toDate,
        name,
        id,
        locale = enUS,
        numberOfMonths = 2,
        showFooterActions = true,
        footerLabels,
        trigger = "field",
    },
    ref,
) {
    const reactId = useId();
    const fieldId = id ?? `ds-daterange-${reactId}`;
    const messageId = `${fieldId}-msg`;
    const isButtonTrigger = trigger === "button";

    const isControlled = onChange !== undefined;
    const [inner, setInner] = useState<DateRangeValue | undefined>(
        defaultValue,
    );
    const selected = (isControlled ? value ?? undefined : inner) as
        | DateRange
        | undefined;

    const hasError = Boolean(error);
    const message = typeof error === "string" ? error : helperText;
    const hasValue = Boolean(selected?.from || selected?.to);

    const [open, setOpen] = useState(false);
    const [menuMonth, setMenuMonth] = useState<Date>(
        selected?.from ?? new Date(),
    );
    const [pending, setPending] = useState<DateRange | undefined>(selected);

    useEffect(() => {
        if (open) {
            setMenuMonth(selected?.from ?? new Date());
            setPending(selected);
        }
    }, [open, selected?.from, selected?.to]);

    const formatRange = (range?: DateRangeValue | null) => {
        if (!range?.from) return "";
        const start = format(range.from, dateFormat, { locale });
        if (!range.to) return start;
        const end = format(range.to, dateFormat, { locale });
        return `${start} - ${end}`;
    };

    const rangeDisplay = formatRange(
        showFooterActions && open ? pending : selected,
    );
    const displayText = isButtonTrigger
        ? rangeDisplay || label
        : rangeDisplay || "\u00a0";

    const footerRangeText = formatRange(showFooterActions ? pending : selected);

    const commitSelection = (range: DateRange | undefined) => {
        const next: DateRangeValue | undefined = range?.from
            ? { from: range.from, to: range.to }
            : undefined;
        if (!isControlled) {
            setInner(next);
        }
        onChange?.(next);
    };

    const handleSelect = (range: DateRange | undefined) => {
        if (showFooterActions) {
            setPending(range);
            return;
        }
        commitSelection(range);
        if (range?.from && range?.to) {
            setOpen(false);
        }
    };

    const handleOpenChange = (nextOpen: boolean) => {
        if (!nextOpen && showFooterActions) {
            // Discard draft when closing without Apply
            setPending(selected);
        }
        setOpen(nextOpen);
    };

    const footerApply = footerLabels?.apply ?? "Apply";
    const footerCancel = footerLabels?.cancel ?? "Cancel";

    const onTriggerKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
        if (disabled) return;
        if (e.key === " " || e.key === "Enter") {
            e.preventDefault();
            setOpen(o => !o);
        }
    };

    const activeRange = showFooterActions ? pending : selected;
    const canApply = Boolean(pending?.from && pending?.to);

    return (
        <div
            className={cx(
                styles.root,
                fullWidth && styles.fullWidth,
                className,
            )}
            data-disabled={disabled || undefined}
            data-error={hasError || undefined}
            data-has-value={hasValue || undefined}
            data-open={open || undefined}
            data-trigger={trigger}
            data-ui="date-range-picker"
        >
            {name ? (
                <>
                    <input
                        aria-hidden
                        name={`${name}_start`}
                        readOnly
                        tabIndex={-1}
                        type="hidden"
                        value={
                            selected?.from
                                ? format(selected.from, "yyyy-MM-dd")
                                : ""
                        }
                    />
                    <input
                        aria-hidden
                        name={`${name}_end`}
                        readOnly
                        tabIndex={-1}
                        type="hidden"
                        value={
                            selected?.to
                                ? format(selected.to, "yyyy-MM-dd")
                                : ""
                        }
                    />
                </>
            ) : null}

            <Popover.Root onOpenChange={handleOpenChange} open={open}>
                <Popover.Trigger asChild>
                    {isButtonTrigger ? (
                        <button
                            aria-describedby={message ? messageId : undefined}
                            aria-expanded={open}
                            aria-haspopup="dialog"
                            aria-invalid={hasError || undefined}
                            aria-label={label}
                            className={styles.buttonTrigger}
                            data-selected={hasValue || undefined}
                            disabled={disabled}
                            id={fieldId}
                            onKeyDown={onTriggerKeyDown}
                            ref={ref}
                            type="button"
                        >
                            <span className={styles.buttonTriggerLabel}>
                                {displayText}
                                {required && !hasValue ? " *" : ""}
                            </span>
                        </button>
                    ) : (
                        <button
                            aria-describedby={message ? messageId : undefined}
                            aria-expanded={open}
                            aria-haspopup="dialog"
                            aria-invalid={hasError || undefined}
                            aria-labelledby={`${fieldId}-lbl ${fieldId}-val`}
                            className={styles.field}
                            disabled={disabled}
                            id={fieldId}
                            onKeyDown={onTriggerKeyDown}
                            ref={ref}
                            type="button"
                        >
                            <span className={styles.inner}>
                                <span
                                    className={styles.display}
                                    id={`${fieldId}-val`}
                                >
                                    {displayText}
                                </span>
                                <span
                                    className={styles.floatingLabel}
                                    id={`${fieldId}-lbl`}
                                >
                                    {label}
                                    {required ? " *" : ""}
                                </span>
                            </span>
                            <span className={styles.iconRight}>
                                <CalendarIcon />
                            </span>
                        </button>
                    )}
                </Popover.Trigger>

                <Popover.Portal>
                    <Popover.Content
                        align="start"
                        className={cx(
                            styles.flyout,
                            numberOfMonths === 2
                                ? styles.flyoutMonths2
                                : styles.flyoutMonths1,
                        )}
                        collisionPadding={16}
                        onOpenAutoFocus={e => e.preventDefault()}
                        side="bottom"
                        sideOffset={4}
                    >
                        <DayPicker
                            disabled={
                                disabled
                                    ? true
                                    : fromDate
                                    ? { before: fromDate }
                                    : undefined
                            }
                            fromDate={fromDate}
                            locale={locale}
                            mode="range"
                            month={menuMonth}
                            numberOfMonths={numberOfMonths}
                            onMonthChange={setMenuMonth}
                            onSelect={handleSelect}
                            selected={activeRange}
                            showOutsideDays
                            toDate={toDate}
                            weekStartsOn={0}
                        />
                        {showFooterActions ? (
                            <div className={styles.footer}>
                                <span className={styles.footerRange}>
                                    {footerRangeText || "\u00a0"}
                                </span>
                                <div className={styles.footerActions}>
                                    <Button
                                        onClick={() => {
                                            setPending(selected);
                                            setOpen(false);
                                        }}
                                        size="sm"
                                        type="button"
                                        variant="secondary"
                                    >
                                        {footerCancel}
                                    </Button>
                                    <Button
                                        disabled={!canApply}
                                        onClick={() => {
                                            commitSelection(pending);
                                            setOpen(false);
                                        }}
                                        size="sm"
                                        type="button"
                                        variant="primary"
                                    >
                                        {footerApply}
                                    </Button>
                                </div>
                            </div>
                        ) : null}
                    </Popover.Content>
                </Popover.Portal>
            </Popover.Root>

            {message ? (
                <div className={styles.helper} id={messageId}>
                    {message}
                </div>
            ) : null}
        </div>
    );
});

DateRangePicker.displayName = "DateRangePicker";
